import {ReactElement, useContext} from 'react'
import {Button} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CircularProgress from '@material-ui/core/CircularProgress'
import {Trans} from 'react-i18next'

import {ModalContainer} from '../ModalContainer'
import AppContext from '../../../../Common/contexts/AppContext'
import {getAccountLabel} from '../../../../../common/utils/accounts'
import messages from '../../../../../assets/messages'
import {Account} from '../../../../../graphql/types'
import {getFormattedAmount} from '../../../../../common/utils'
import {WithdrawalMethodValues} from '../constants'
import {useStyles} from './styles'

type ConfirmSubmitModalProps = {
  account: Account
  isSubmitting: boolean
  isOpen: boolean
  activeWithdrawalMethod: WithdrawalMethodValues | null
  totalRemainingAmount: number
  totalAmountToRefund: number
  bankTransferPaymentFields: Record<string, any> | null
  cryptoPaymentFields: Record<string, any> | null
  cryptoPayoutPaymentFields: Record<string, any> | null
  payRetailersPaymentFields: Record<string, any> | null
  pixPayoutPaymentFields: Record<string, any> | null
  onClose: () => void
  onRequestSend: () => void
}

function ConfirmSubmitModal(props: ConfirmSubmitModalProps): ReactElement {
  const {
    account,
    isSubmitting,
    isOpen,
    activeWithdrawalMethod,
    totalRemainingAmount,
    totalAmountToRefund,
    bankTransferPaymentFields,
    cryptoPaymentFields,
    cryptoPayoutPaymentFields,
    payRetailersPaymentFields,
    pixPayoutPaymentFields,
    onClose,
    onRequestSend,
  } = props
  const classes = useStyles()

  const {locale} = useContext(AppContext)
  const accountLabel = `${getAccountLabel(account, locale)} (${account.remoteId})`

  const isBankTransferReady =
    activeWithdrawalMethod === WithdrawalMethodValues.bankTransfer && bankTransferPaymentFields
  const isCryptoTransferReady =
    activeWithdrawalMethod === WithdrawalMethodValues.cryptocurrency && cryptoPaymentFields
  const isCryptoPayoutTransferReady =
    activeWithdrawalMethod === WithdrawalMethodValues.enovipayCryptoPayout &&
    cryptoPayoutPaymentFields
  const isPayRetailersPaymentFieldsReady =
    activeWithdrawalMethod === WithdrawalMethodValues.payRetailers && payRetailersPaymentFields
  const isPixPayoutFieldsReady =
    activeWithdrawalMethod === WithdrawalMethodValues.enovipayPixPayout && pixPayoutPaymentFields

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  return (
    <ModalContainer isOpen={isOpen} onClose={handleClose}>
      <div className={classes.content}>
        <div className={classes.header}>
          <CloseIcon className={classes.closeIcon} onClick={handleClose} />
          <div className={classes.title}>
            <Trans {...messages.reviewWithdrawalDetails} />
          </div>
          <div className={classes.subtitle}>
            {totalAmountToRefund > 0 ? (
              <div>
                <Trans
                  {...messages.depositWillBeReturned}
                  values={{
                    amount: getFormattedAmount({
                      amount: totalAmountToRefund,
                      currency: account.currency,
                    }),
                  }}
                  components={[<b>amount</b>]}
                />
              </div>
            ) : null}
            {isBankTransferReady ||
            isCryptoTransferReady ||
            isCryptoPayoutTransferReady ||
            isPixPayoutFieldsReady ||
            isPayRetailersPaymentFieldsReady ? (
              <div>
                <Trans {...messages.reviewTheDetailsBelow} />:
              </div>
            ) : null}
          </div>
        </div>
        <div className={classes.body}>
          <div className={classes.row}>
            <div className={classes.label}>
              <Trans {...messages.account} />:
            </div>
            <div>{accountLabel}</div>
          </div>
          {totalRemainingAmount > 0 ? (
            <div className={classes.row}>
              <div className={classes.label}>
                <Trans {...messages.amount} />:
              </div>
              <div>
                {getFormattedAmount({amount: totalRemainingAmount, currency: account.currency})}
              </div>
            </div>
          ) : null}
          {activeWithdrawalMethod === WithdrawalMethodValues.bankTransfer &&
          bankTransferPaymentFields ? (
            <>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.bankAccountIBAN} />:
                </div>
                <div>{bankTransferPaymentFields.iban}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.swiftBicCode} />:
                </div>
                <div>{bankTransferPaymentFields.swiftCode}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.bankAccountHolderName} />:
                </div>
                <div>{bankTransferPaymentFields.bankAccountHolderName}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.bankName} />:
                </div>
                <div>{bankTransferPaymentFields.bankName}</div>
              </div>
            </>
          ) : null}
          {activeWithdrawalMethod === WithdrawalMethodValues.payRetailers &&
          payRetailersPaymentFields ? (
            <>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.bank} />
                </div>
                <div>{payRetailersPaymentFields.bankCode}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.personalIdentificationNumber} />
                </div>
                <div>{payRetailersPaymentFields.document}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.documentType} />
                </div>
                <div>{payRetailersPaymentFields.documentType}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.accountType} />
                </div>
                <div>{payRetailersPaymentFields.bankAccountType}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.bankAccount} />
                </div>
                <div>{payRetailersPaymentFields.bankAccountNumber}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.email} />
                </div>
                <div>{payRetailersPaymentFields.beneficiaryEmail}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.firstName} />
                </div>
                <div>{payRetailersPaymentFields.beneficiaryFirstName}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.lastName} />
                </div>
                <div>{payRetailersPaymentFields.beneficiaryLastName}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.phoneNumber} />
                </div>
                <div>{payRetailersPaymentFields.beneficiaryPhone}</div>
              </div>
            </>
          ) : null}
          {activeWithdrawalMethod === WithdrawalMethodValues.cryptocurrency &&
          cryptoPaymentFields ? (
            <>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.address} />:
                </div>
                <div>{cryptoPaymentFields.walletAddress}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.network} />:
                </div>
                <div>{cryptoPaymentFields.cryptoNetwork}</div>
              </div>
            </>
          ) : null}
          {activeWithdrawalMethod === WithdrawalMethodValues.enovipayCryptoPayout &&
          cryptoPayoutPaymentFields ? (
            <>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.address} />:
                </div>
                <div>{cryptoPayoutPaymentFields.walletAddress}</div>
              </div>
              <div className={classes.row}>
                <div className={classes.label}>
                  <Trans {...messages.network} />:
                </div>
                <div>{cryptoPayoutPaymentFields.cryptoNetwork}</div>
              </div>
            </>
          ) : null}
          <div className={classes.footer}>
            <Button
              className={classes.button}
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleClose}
            >
              <Trans {...messages.edit} />
            </Button>
            <Button
              disabled={isSubmitting}
              className={classes.button}
              fullWidth
              variant="contained"
              color="primary"
              onClick={onRequestSend}
            >
              {isSubmitting ? (
                <CircularProgress className={classes.loader} size={12} />
              ) : (
                <Trans {...messages.submitRequest} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </ModalContainer>
  )
}

export {ConfirmSubmitModal}
